// /preview/:cellPath — Phase 1 read-only viewer.
// Left pane: hierarchical cell tree (shared via CellTreeShell).
// Right pane: ReadOnlyEditor seeded from cell-loader's build-time glob snapshot.
//
// ReadOnlyEditor is keyed by cellPath. MDXEditor's `markdown` prop is read
// once at mount; keying forces a fresh mount on every navigation.

// MDXEditor's stylesheet must load before tailwind so design-system
// tokens win on any conflicts. Imported here (not main.tsx) so it is
// only fetched when an admin navigates to /preview — keeping it out
// of the investor entry bundle.
import '@mdxeditor/editor/style.css';
import { useEffect, useState } from 'react';
import { ReadOnlyEditor } from '@rmint/editor';
import { loadCell } from '../lib/cell-loader';
import { CellTreeShell } from '../components/CellTreeShell';

// Inner pane: loads markdown for the selected cell and renders ReadOnlyEditor.
function PreviewPane({ cellPath }: { cellPath: string }) {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMarkdown(null);
    setError(null);
    if (!cellPath) return;
    let cancelled = false;
    loadCell(cellPath)
      .then((md) => {
        if (!cancelled) setMarkdown(md);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [cellPath]);

  if (!cellPath) return <p>Select a cell on the left.</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (markdown === null) return <p className="text-foreground-soft">Loading…</p>;
  return <ReadOnlyEditor key={cellPath} markdown={markdown} />;
}

export function Preview() {
  return <CellTreeShell rightPane={PreviewPane} routePrefix="preview" />;
}
